import { Avatar } from "@chakra-ui/react";
import type { AvatarProps } from "@chakra-ui/react";
import { useEffect, useState } from "react";

interface UserAvatarProps extends Omit<AvatarProps, "src"> {
  name: string;
  email?: string;
  src?: string;
}

const _cache = new Map<string, string | null>();

// Queue global: no máximo 2 requests simultâneos para unavatar.io
const MAX_CONCURRENT = 2;
let _active = 0;
const _queue: Array<() => void> = [];

function runNext() {
  if (_active >= MAX_CONCURRENT || _queue.length === 0) return;
  const task = _queue.shift()!;
  _active++;
  task();
}

function enqueue(task: () => Promise<void>) {
  _queue.push(() => {
    task().finally(() => {
      _active--;
      runNext();
    });
  });
  runNext();
}

export const UserAvatar = ({ name, email, src: directSrc, ...props }: UserAvatarProps) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(() => {
    if (directSrc !== undefined) return directSrc;
    if (email && _cache.has(email)) return _cache.get(email) ?? undefined;
    return undefined;
  });

  useEffect(() => {
    if (directSrc !== undefined) {
      setResolvedSrc(directSrc);
      return;
    }
    if (!email) return;

    if (_cache.has(email)) {
      setResolvedSrc(_cache.get(email) ?? undefined);
      return;
    }

    const url = `https://unavatar.io/${encodeURIComponent(email)}`;

    enqueue(async () => {
      if (_cache.has(email)) {
        setResolvedSrc(_cache.get(email) ?? undefined);
        return;
      }
      try {
        const res = await fetch(url, { method: "HEAD" });
        const ct = res.headers.get("content-type") ?? "";
        const isReal = res.ok && (ct.includes("jpeg") || ct.includes("webp"));
        const resolved = isReal ? url : null;
        _cache.set(email, resolved);
        if (resolved) setResolvedSrc(resolved);
        else _cache.set(email, null);
      } catch {
        _cache.set(email, null);
      }
    });
  }, [email, directSrc]);

  return <Avatar name={name} src={resolvedSrc} {...props} />;
};
